import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Play, ShieldAlert, Users, Plane, Calendar, Info, Shield,
  TrendingDown, TrendingUp, ExternalLink, Loader2,
} from "lucide-react";
import SuggestedCandidates from "@/pages/SuggestedCandidates";

// Coverage Simulation (read-only).
// This is a single self-contained tab. It calls:
// GET  /api/simulations/coverage/latest  on mount
// POST /api/simulations/coverage/run     when the user clicks "Run"
// And renders three sections: KPI tiles, per-day gap timeline, per-policy table.

const ICON_BY_TYPE = {
  policy: Shield,
  assigned: Users,
  absence: Plane,
  context: Calendar,
};

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoDaysFromToday(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDateLong(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-GB", {
    weekday: "long", day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtMinutes(mins) {
  if (!mins || mins <= 0) return "0 m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} m`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} m`;
}

function healthTone(pct) {
  if (pct >= 95) return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" };
  if (pct >= 80) return { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-500" };
  return            { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    dot: "bg-rose-500" };
}

export default function CoverageSimulationTab() {
  const [fromDate, setFromDate] = useState(isoToday());
  const [toDate, setToDate] = useState(isoDaysFromToday(6));
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [showOk, setShowOk] = useState(false);

  // Fetch latest on mount for these dates if it exists; non-fatal on 404.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/simulations/coverage/latest?from_date=${fromDate}&to_date=${toDate}`
        );
        if (!cancelled) setSim(data);
      } catch (err) {
        if (err?.response?.status !== 404 && !cancelled) {
          toast.error(formatError(err.response?.data?.detail) || "Failed to load latest simulation");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (toDate < fromDate) {
      toast.error("End date must be on or after start date.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/simulations/coverage/run", {
        from_date: fromDate, to_date: toDate,
      });
      setSim(data);
      if (data.status === "completed") {
        toast.success("Coverage simulation complete");
      } else {
        toast.error(`Simulation ${data.status}: ${data.error_message || "see history"}`);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to run simulation");
    } finally {
      setBusy(false);
    }
  };

  const summary = sim?.result_summary || null;
  const raw = sim?.raw_result || null;
  const cells = raw?.cells || [];
  const policySummary = raw?.policy_summary || [];
  const limitations = raw?.limitations || [];

  // Group cells by date
  const cellsByDate = useMemo(() => {
    const out = {};
    for (const c of cells) {
      if (!showOk && c.verdict === "ok") continue;
      (out[c.date] ||= []).push(c);
    }
    return out;
  }, [cells, showOk]);
  const datesSorted = useMemo(
    () => Object.keys(cellsByDate).sort(), [cellsByDate]
  );

  return (
    <div className="space-y-5" data-testid="coverage-sim-tab">
      {/* Help / limitations banner */}
      <div className="rounded-lg border border-[#BFD3FB] bg-[#F0F6FF] p-3 flex gap-3"
           data-testid="coverage-sim-help">
        <Info className="h-4 w-4 text-[#1D4ED8] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-[#1D4ED8] leading-relaxed">
          <span className="font-medium">What does this check?</span>{" "}
          It evaluates each active Coverage Policy against the live rota for the
          chosen window and reports days where your minimum staffing isn&apos;t met
          (or where you&apos;re over the maximum). The simulation is read-only, nothing
          on your rota, leave or assignments is changed.
          <br />
          <span className="font-medium">v1 limitation:</span> This check counts an
          employee as covering a policy if their shift overlaps the policy&apos;s
          time window at all on that day. It does not yet detect sub-day gaps,
          such as an uncovered hour in the middle of the shift.
        </div>
      </div>

      {/* Control bar */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3"
           data-testid="coverage-sim-controls">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="cov-from">From</Label>
          <Input id="cov-from" type="date" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 className="mt-1 w-44" data-testid="coverage-sim-from" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="cov-to">To</Label>
          <Input id="cov-to" type="date" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 className="mt-1 w-44" data-testid="coverage-sim-to" />
        </div>
        <Button onClick={run} disabled={busy}
                data-testid="coverage-sim-run"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {busy ? "Running…" : "Run simulation"}
        </Button>
        {sim?.created_at && (
          <div className="text-[11px] text-[#64748B] ml-auto">
            Last run: {new Date(sim.created_at).toLocaleString("en-GB", {
              day: "2-digit", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit", hour12: false,
            })}
            {sim.engine_version && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]">
                engine {sim.engine_version}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Empty / first-run state */}
      {!summary && !busy && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center"
             data-testid="coverage-sim-empty">
          <ShieldAlert className="h-7 w-7 text-[#2563EB] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#0B1A36]">No coverage simulation yet for this window</p>
          <p className="text-xs text-[#4B5773] mt-1">Pick a date range above and click <strong>Run simulation</strong> to see your coverage health.</p>
        </div>
      )}

      {summary && summary.setup_state && summary.setup_state !== "ready" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-3"
             data-testid={`coverage-setup-state-${summary.setup_state}`}>
          <Info className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <span className="font-medium">Heads-up:</span>{" "}
            {summary.setup_state_message}
          </div>
        </div>
      )}

      {summary && (
        <CoverageKpiStrip summary={summary} />
      )}

      {summary && cells.length === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 text-sm"
             data-testid="coverage-sim-no-policies">
          No coverage policies applied for this window. Add policies on the
          Coverage Policies page to get a coverage health score.
        </div>
      )}

      {summary && cells.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl">Coverage timeline</h3>
            <label className="text-xs text-[#4B5773] inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showOk}
                     onChange={(e) => setShowOk(e.target.checked)}
                     className="accent-[#2563EB]"
                     data-testid="coverage-sim-show-ok" />
              Show fully-covered days
            </label>
          </div>
          <div className="space-y-3" data-testid="coverage-sim-timeline">
            {datesSorted.length === 0 ? (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                Every covered day in this window meets its minimum staffing. Nice work.
              </div>
            ) : datesSorted.map((d) => (
              <DayGroup key={d} date={d} cells={cellsByDate[d]} fromDate={fromDate} toDate={toDate} />
            ))}
          </div>

          <PolicySummaryTable rows={policySummary} />
        </>
      )}

      {limitations.length > 0 && summary && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-[#475569]"
             data-testid="coverage-sim-limitations">
          <div className="font-medium text-[#0B1A36] mb-1">What this v1 check does not yet do</div>
          <ul className="list-disc list-inside space-y-0.5">
            {limitations.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function CoverageKpiStrip({ summary }) {
  const pct = summary.coverage_health_pct ?? 100;
  const tone = healthTone(pct);
  const tiles = [
    {
      key: "health",
      label: "Coverage health",
      value: `${pct}%`,
      tone,
      hint: "Percentage of staffing requirements currently being met.",
    },
    {
      key: "gaps",
      label: "Days with gaps",
      value: `${summary.gap_cells || 0}`,
      tone: (summary.gap_cells || 0) === 0 ? healthTone(100) : healthTone(60),
      hint: "Days where at least one staffing requirement falls below minimum coverage.",
    },
    {
      key: "short",
      label: "Total time short",
      value: fmtMinutes(summary.total_minutes_short || 0),
      tone: (summary.total_minutes_short || 0) === 0 ? healthTone(100) : healthTone(60),
      hint: "Combined uncovered staffing time across all identified gaps.",
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="coverage-sim-kpis">
      {tiles.map((t) => (
        <div key={t.key} title={t.hint}
             className={`rounded-lg border p-4 ${t.tone.bg} ${t.tone.border}`}>
          <div className={`text-[11px] uppercase tracking-widest ${t.tone.text}`}>{t.label}</div>
          <div className={`text-3xl font-display mt-1 ${t.tone.text}`} data-testid={`coverage-kpi-${t.key}`}>{t.value}</div>
          <div className="text-[11px] text-[#64748B] mt-1">{t.hint}</div>
        </div>
      ))}
    </div>
  );
}

function DayGroup({ date, cells, fromDate, toDate }) {
  const shortCount = cells.filter((c) => c.verdict === "short").length;
  const overCount = cells.filter((c) => c.verdict === "over").length;
  const totalMinutes = cells.reduce((a, c) => a + (c.minutes_short || 0), 0);
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`coverage-day-${date}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
        <div className="text-sm font-medium text-[#0B1A36]">{fmtDateLong(date)}</div>
        <div className="text-xs text-[#4B5773]">
          {shortCount > 0 && <span className="text-rose-700 font-medium">{shortCount} gap{shortCount === 1 ? "" : "s"}</span>}
          {shortCount > 0 && overCount > 0 && <span className="mx-1">·</span>}
          {overCount > 0 && <span className="text-amber-700 font-medium">{overCount} over</span>}
          {shortCount === 0 && overCount === 0 && <span className="text-emerald-700 font-medium">Fully covered</span>}
          {totalMinutes > 0 && <span className="ml-2 text-[#64748B]">· {fmtMinutes(totalMinutes)} short</span>}
        </div>
      </div>
      <ol className="divide-y divide-slate-100">
        {cells.map((c) => <CellRow key={c.cell_id} cell={c} fromDate={fromDate} toDate={toDate} />)}
      </ol>
    </div>
  );
}

function CellRow({ cell, fromDate, toDate }) {
  const isShort = cell.verdict === "short";
  const isOver = cell.verdict === "over";
  const railClass =
    isShort ? "bg-rose-500" :
    isOver ? "bg-amber-500" :
    "bg-emerald-500";
  const verdictLabel =
    isShort ? `Short by ${cell.shortfall}` :
    isOver ? `Over by ${cell.surplus}` :
    "Coverage met";
  const verdictColour =
    isShort ? "text-rose-700" :
    isOver ? "text-amber-700" :
    "text-emerald-700";
  return (
    <li className="px-4 py-3" data-testid={`coverage-cell-${cell.cell_id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-1 self-stretch rounded ${railClass}`} aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-sm font-medium ${verdictColour}`}>{verdictLabel}</span>
            <span className="text-sm text-[#0B1A36]">- {cell.policy.name}</span>
            <span className="text-xs text-[#64748B]">
              {cell.policy.category_name}
              {cell.policy.location_name && ` · ${cell.policy.location_name}`}
              {cell.policy.team_name && ` · ${cell.policy.team_name}`}
              {cell.policy.window && ` · ${cell.policy.window}`}
            </span>
          </div>

          <ul className="mt-2 space-y-1">
            {cell.explain.map((line, idx) => {
              const Icon = ICON_BY_TYPE[line.type] || Info;
              return (
                <li key={idx} className="flex items-start gap-2 text-xs text-[#4B5773]">
                  <Icon className="h-3.5 w-3.5 mt-0.5 text-[#64748B] flex-shrink-0" />
                  <span>{line.text}</span>
                </li>
              );
            })}
          </ul>

          {cell.would_close_the_gap && (
            <div className="mt-2 text-xs text-[#475569] bg-slate-50 border border-slate-200 rounded p-2"
                 data-testid={`coverage-cell-fix-${cell.cell_id}`}>
              <strong className="text-[#0B1A36]">Suggested next step:</strong> {cell.would_close_the_gap.suggested_action}
            </div>
          )}

          {cell.deep_links?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-3">
              {cell.deep_links.map((dl) => (
                <a key={dl.href} href={dl.href}
                   className="text-xs text-[#1D4ED8] hover:underline inline-flex items-center gap-1"
                   data-testid={`coverage-cell-link-${cell.cell_id}-${dl.label.split(" ")[0].toLowerCase()}`}>
                  <ExternalLink className="h-3 w-3" />{dl.label}
                </a>
              ))}
            </div>
          )}

          {isShort && (
            <SuggestedCandidates
              cellId={cell.cell_id}
              fromDate={fromDate}
              toDate={toDate}
            />
          )}
        </div>
      </div>
    </li>
  );
}

function PolicySummaryTable({ rows }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="coverage-policy-summary">
      <button type="button"
              className="w-full px-4 py-2.5 flex items-center justify-between text-sm text-[#0B1A36] hover:bg-slate-50"
              onClick={() => setOpen((o) => !o)}
              data-testid="coverage-policy-summary-toggle">
        <span className="font-medium">Per-policy roll-up ({rows.length})</span>
        <span className="text-xs text-[#64748B]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-[#64748B]">
              <tr>
                <th className="text-left px-4 py-2">Policy</th>
                <th className="text-right px-4 py-2">Health %</th>
                <th className="text-right px-4 py-2">Days evaluated</th>
                <th className="text-right px-4 py-2">Gap days</th>
                <th className="text-right px-4 py-2">Over days</th>
                <th className="text-right px-4 py-2">Time short</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const tone = healthTone(r.health_pct ?? 100);
                return (
                  <tr key={r.coverage_id} className="border-t border-slate-100"
                      data-testid={`coverage-policy-row-${r.coverage_id}`}>
                    <td className="px-4 py-2">{r.name}</td>
                    <td className={`px-4 py-2 text-right font-medium ${tone.text}`}>{r.health_pct ?? 100}%</td>
                    <td className="px-4 py-2 text-right text-[#4B5773]">{r.evaluated_days}</td>
                    <td className="px-4 py-2 text-right text-rose-700">{r.gap_days}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{r.over_days}</td>
                    <td className="px-4 py-2 text-right text-[#4B5773]">{fmtMinutes(r.total_minutes_short)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
